import { DeepPartial } from '../util/types';
import z, { boolean as Boolean, object as Object, string as String } from 'zod';

// Hybrid is a combination of firebase and apigee
// use 'Firebase' for local api integration (does not use Apigee)
const AuthType = z.literal('Hybrid').or(z.literal('Firebase'));

const ConfigSchema = Object({
  env: String(),
  googleTagManager: Object({
    oPowerWidgetCode: String(),
  }),
  storageKey: String(),
  graphql: Object({
    baseUrl: String(),
    baseUrlPgePlus: String(),
  }),
  apigee: Object({
    baseUrl: String(),
    auth: Object({
      url: String(),
      clientId: String(),
    }),
    widgetAuth: Object({
      clientId: String(),
    }),
  }),
  apm: Object({
    active: Boolean(),
    serverUrl: String(),
  }),
  auth: Object({
    type: AuthType,
  }),
  contentful: Object({
    host: String(),
    spaceId: String(),
    environmentName: String(),
    accessToken: String(),
  }),
  firebase: Object({
    key: String(),
    databaseURL: String(),
    reCaptchaSiteKey: String(),
    projectId: String(),
    appId: String(),
    installerTenentId: String(),
    invoicePortalTenentId: String(),
  }),
  legacySiteURL: String(),
  bizWebLegacyURL: String(),
  bizApiLegacyURL: String(),
  csApiLegacyURL: String(),
  salseforceEVFormURL: String(),
  amazonLocationService: Object({
    key: String(),
    region: String(),
  }),
});

type Config = z.infer<typeof ConfigSchema>;

const APIGEE_GENERIC_PATH = '/pge-graphql';

const rawConfig: DeepPartial<Config> = {
  env: process.env.GATSBY_ACTIVE_ENV,
  auth: {
    type:
      (process.env.GATSBY_AUTH_TYPE as z.infer<typeof AuthType>) || 'Hybrid',
  },
  googleTagManager: {
    oPowerWidgetCode: process.env.GATSBY_GTM_WORKSPACE_ID,
  },
  storageKey: 'p2s5u8x/A?D(G+KbPeShVmYq3t6w9y$B',
  graphql: {
    baseUrl: `${process.env.GATSBY_APIGEE_URL}`,
    baseUrlPgePlus: `${process.env.GATSBY_APIGEE_URL_PGEPLUS}`,
  },
  apigee: {
    baseUrl: process.env.GATSBY_APIGEE_URL,
    auth: {
      url: process.env.GATSBY_APIGEE_URL?.split(APIGEE_GENERIC_PATH)?.[0],
      clientId: process.env.GATSBY_APIGEE_AUTH_CLIENT_ID,
    },
    widgetAuth: {
      clientId: process.env.GATSBY_APIGEE_WIDGETAUTH_CLIENT_ID,
    },
  },
  apm: {
    active: process.env.GATSBY_APM_ACTIVE === 'true',
    serverUrl: process.env.GATSBY_APM_URL,
  },
  contentful: {
    host: process.env.GATSBY_REALTIME_CONTENTFUL_HOST,
    spaceId: process.env.GATSBY_REALTIME_CONTENTFUL_SPACE_ID,
    environmentName: process.env.GATSBY_REALTIME_CONTENTFUL_ENVIRONMENT_NAME,
    accessToken: process.env.GATSBY_REALTIME_CONTENTFUL_ACCESS_TOKEN,
  },
  firebase: {
    key: process.env.GATSBY_FIREBASE_API_KEY,
    databaseURL: process.env.GATSBY_FIREBASE_DATABASE_URL,
    reCaptchaSiteKey: process.env.GATSBY_FIREBASE_RECAPTCHA_SITE_KEY,
    projectId: process.env.GATSBY_FIREBASE_PROJECT_ID,
    appId: process.env.GATSBY_FIREBASE_APP_ID,
    installerTenentId: process.env.GATSBY_FIREBASE_INSTALLER_TENENT_ID,
    invoicePortalTenentId: process.env.GATSBY_FIREBASE_INVOICE_PORTAL_TENENT_ID,
  },
  legacySiteURL: process.env.GATSBY_LEGACY_URL_SITE,
  bizWebLegacyURL: process.env.GATSBY_LEGACY_URL_BIZ_WEB,
  bizApiLegacyURL: process.env.GATSBY_LEGACY_URL_BIZ_API,
  csApiLegacyURL: process.env.GATSBY_LEGACY_URL_CS_API,
  salseforceEVFormURL: process.env.GATSBY_SALESFORCE_EV_FORM_URL,
  amazonLocationService: {
    key: process.env.GATSBY_AMAZON_LOCATION_SERVICE_KEY,
    region: process.env.GATSBY_AMAZON_LOCATION_SERVICE_REGION,
  },
};

const config = ConfigSchema.parse(rawConfig);

export default config;
